// components/legal/LegalAgreement.jsx
import React, { useState } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase-config';
import { useNavigate } from 'react-router-dom';
import { RequestASongLogo } from '../components/common/RequestASongLogo';

function LegalAgreement() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    phoneNumber: ''
  });
  const [agreements, setAgreements] = useState({
    terms: false,
    privacy: false,
    communications: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAgreementChange = (e) => {
    setAgreements({
      ...agreements,
      [e.target.name]: e.target.checked
    });
  };

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.displayName || !formData.phoneNumber) {
      setError('Please fill in all required fields');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (!agreements.terms || !agreements.privacy) {
      setError('You must accept the Terms & Conditions and Privacy Policy to continue');
      return false;
    }

    return true;
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // Create user account
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Store musician profile with legal acceptance
      await setDoc(doc(db, 'musicians', userCredential.user.uid), {
        displayName: formData.displayName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        createdAt: serverTimestamp(),
        // Legal compliance tracking
        legalAgreements: {
          termsAccepted: true,
          termsAcceptedAt: serverTimestamp(),
          termsVersion: '2.0',
          privacyAccepted: true,
          privacyAcceptedAt: serverTimestamp(),
          privacyVersion: '2.0',
          communicationsConsent: agreements.communications,
          ipAddress: await getUserIP(),
          userAgent: navigator.userAgent
        },
        // Profile setup status
        profileComplete: false,
        songsAdded: false,
        qrCodeGenerated: false
      });

      navigate('/');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Get user IP for compliance audit trail
  const getUserIP = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      return 'unknown';
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px 16px'
    }}>
      <div style={{
        maxWidth: '672px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '32px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ margin: '0 auto 24px', height: '64px', width: 'auto' }}>
            <RequestASongLogo size={64} showText={true} />
          </div>
          <h2 style={{
            marginTop: '24px',
            fontSize: '30px',
            fontWeight: '800',
            color: 'white'
          }}>
            Join Request a Song
          </h2>
          <p style={{
            marginTop: '8px',
            fontSize: '14px',
            color: '#d1d5db'
          }}>
            Connect with your audience through live music requests
          </p>
        </div>

        <form style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          padding: '32px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px'
        }} onSubmit={handleSignup}>
          {error && (
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              padding: '12px 16px',
              borderRadius: '6px'
            }}>
              {error}
            </div>
          )}

          {/* User Information */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '500',
              color: '#111827'
            }}>Account Information</h3>

            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Display Name *
              </label>
              <input
                type="text"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                style={{
                  marginTop: '4px',
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  outline: 'none',
                  fontSize: '16px'
                }}
                placeholder="How you'll appear to audiences"
                required
                onFocus={(e) => {
                  e.target.style.outline = 'none';
                  e.target.style.boxShadow = '0 0 0 3px rgba(147, 51, 234, 0.1)';
                  e.target.style.borderColor = '#8b5cf6';
                }}
                onBlur={(e) => {
                  e.target.style.boxShadow = 'none';
                  e.target.style.borderColor = '#d1d5db';
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                style={{
                  marginTop: '4px',
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  outline: 'none',
                  fontSize: '16px'
                }}
                required
                onFocus={(e) => {
                  e.target.style.outline = 'none';
                  e.target.style.boxShadow = '0 0 0 3px rgba(147, 51, 234, 0.1)';
                  e.target.style.borderColor = '#8b5cf6';
                }}
                onBlur={(e) => {
                  e.target.style.boxShadow = 'none';
                  e.target.style.borderColor = '#d1d5db';
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Phone Number * (for SMS notifications)
              </label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                style={{
                  marginTop: '4px',
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  outline: 'none',
                  fontSize: '16px'
                }}
                placeholder="+61 4XX XXX XXX"
                required
                onFocus={(e) => {
                  e.target.style.outline = 'none';
                  e.target.style.boxShadow = '0 0 0 3px rgba(147, 51, 234, 0.1)';
                  e.target.style.borderColor = '#8b5cf6';
                }}
                onBlur={(e) => {
                  e.target.style.boxShadow = 'none';
                  e.target.style.borderColor = '#d1d5db';
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Password *
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  style={{
                    marginTop: '4px',
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    outline: 'none',
                    fontSize: '16px'
                  }}
                  required
                  onFocus={(e) => {
                    e.target.style.outline = 'none';
                    e.target.style.boxShadow = '0 0 0 3px rgba(147, 51, 234, 0.1)';
                    e.target.style.borderColor = '#8b5cf6';
                  }}
                  onBlur={(e) => {
                    e.target.style.boxShadow = 'none';
                    e.target.style.borderColor = '#d1d5db';
                  }}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Confirm Password *
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  style={{
                    marginTop: '4px',
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    outline: 'none',
                    fontSize: '16px'
                  }}
                  required
                  onFocus={(e) => {
                    e.target.style.outline = 'none';
                    e.target.style.boxShadow = '0 0 0 3px rgba(147, 51, 234, 0.1)';
                    e.target.style.borderColor = '#8b5cf6';
                  }}
                  onBlur={(e) => {
                    e.target.style.boxShadow = 'none';
                    e.target.style.borderColor = '#d1d5db';
                  }}
                />
              </div>
            </div>
          </div>

          {/* Legal Agreements */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            borderTop: '1px solid #e5e7eb',
            paddingTop: '24px'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '500',
              color: '#111827'
            }}>Legal Agreements</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <input
                  type="checkbox"
                  name="terms"
                  checked={agreements.terms}
                  onChange={handleAgreementChange}
                  style={{
                    marginTop: '4px',
                    height: '16px',
                    width: '16px',
                    color: '#8b5cf6',
                    borderColor: '#d1d5db',
                    borderRadius: '4px'
                  }}
                  required
                />
                <span style={{
                  fontSize: '14px',
                  color: '#374151'
                }}>
                  I have read and agree to the{' '}
                  <a
                    href="/terms?userType=musician"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#8b5cf6',
                      textDecoration: 'underline'
                    }}
                    onMouseOver={(e) => e.target.style.color = '#7c3aed'}
                    onMouseOut={(e) => e.target.style.color = '#8b5cf6'}
                  >
                    Terms and Conditions for Musicians
                  </a>
                  <span style={{ color: '#dc2626', marginLeft: '4px' }}>*</span>
                </span>
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <input
                  type="checkbox"
                  name="privacy"
                  checked={agreements.privacy}
                  onChange={handleAgreementChange}
                  style={{
                    marginTop: '4px',
                    height: '16px',
                    width: '16px',
                    color: '#8b5cf6',
                    borderColor: '#d1d5db',
                    borderRadius: '4px'
                  }}
                  required
                />
                <span style={{
                  fontSize: '14px',
                  color: '#374151'
                }}>
                  I have read and agree to the{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#8b5cf6',
                      textDecoration: 'underline'
                    }}
                    onMouseOver={(e) => e.target.style.color = '#7c3aed'}
                    onMouseOut={(e) => e.target.style.color = '#8b5cf6'}
                  >
                    Privacy Policy
                  </a>
                  <span style={{ color: '#dc2626', marginLeft: '4px' }}>*</span>
                </span>
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <input
                  type="checkbox"
                  name="communications"
                  checked={agreements.communications}
                  onChange={handleAgreementChange}
                  style={{
                    marginTop: '4px',
                    height: '16px',
                    width: '16px',
                    color: '#8b5cf6',
                    borderColor: '#d1d5db',
                    borderRadius: '4px'
                  }}
                />
                <span style={{
                  fontSize: '14px',
                  color: '#374151'
                }}>
                  I consent to receiving promotional emails and platform updates
                  (you can unsubscribe at any time)
                </span>
              </label>
            </div>

            <div style={{
              backgroundColor: '#dbeafe',
              border: '1px solid #93c5fd',
              borderRadius: '6px',
              padding: '16px'
            }}>
              <p style={{
                fontSize: '12px',
                color: '#1e40af',
                margin: 0
              }}>
                <strong>Your Rights:</strong> You can access, update, or delete your personal information at any time.
                You can withdraw consent for marketing communications.
                Contact privacy@requestasong.com for any privacy-related questions.
              </p>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !agreements.terms || !agreements.privacy}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              padding: '12px 16px',
              border: 'none',
              borderRadius: '6px',
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
              fontSize: '14px',
              fontWeight: '500',
              color: 'white',
              backgroundColor: loading || !agreements.terms || !agreements.privacy ? '#9ca3af' : '#8b5cf6',
              cursor: loading || !agreements.terms || !agreements.privacy ? 'not-allowed' : 'pointer',
              outline: 'none',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => {
              if (!loading && agreements.terms && agreements.privacy) {
                e.target.style.backgroundColor = '#7c3aed';
              }
            }}
            onMouseOut={(e) => {
              if (!loading && agreements.terms && agreements.privacy) {
                e.target.style.backgroundColor = '#8b5cf6';
              }
            }}
            onFocus={(e) => {
              e.target.style.outline = 'none';
              e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
            }}
          >
            {loading ? 'Creating Account...' : 'Create Musician Account'}
          </button>

          <div style={{ textAlign: 'center' }}>
            <p style={{
              fontSize: '14px',
              color: '#6b7280'
            }}>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#8b5cf6',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                onMouseOver={(e) => e.target.style.color = '#7c3aed'}
                onMouseOut={(e) => e.target.style.color = '#8b5cf6'}
              >
                Sign in here
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

export default LegalAgreement;